/**
 * Turkish render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'tr')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const turkishLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'bulanık' },
    change: { primary: 'değişim' },
    click: { primary: 'tıklama' },
    dblclick: { primary: 'dblclick' },
    focus: { primary: 'odak' },
    input: { primary: 'giriş' },
    keydown: { primary: 'tuşbasma' },
    keypress: { primary: 'keypress' },
    keyup: { primary: 'tuşbırakma' },
    load: { primary: 'yükle' },
    mousedown: { primary: 'farebas' },
    mouseenter: { primary: 'mouseenter' },
    mouseleave: { primary: 'mouseleave' },
    mousemove: { primary: 'mousemove' },
    mouseout: { primary: 'faredışında' },
    mouseover: { primary: 'fareiçinde' },
    mouseup: { primary: 'farebırak' },
    reset: { primary: 'sıfırla' },
    resize: { primary: 'boyutlandırma' },
    scroll: { primary: 'kaydır' },
    submit: { primary: 'gönderme' },
    touchcancel: { primary: 'touchcancel' },
    touchend: { primary: 'touchend' },
    touchmove: { primary: 'touchmove' },
    touchstart: { primary: 'touchstart' },
    unload: { primary: 'unload' },
  },
  logical: {
    and: { primary: 've' },
    changes: { primary: 'değiştiğinde' },
    contains: { primary: 'içerir' },
    else: { primary: 'yoksa' },
    end: { primary: 'son' },
    equals: { primary: 'eşittir' },
    exists: { primary: 'var' },
    has: { primary: 'var' },
    have: { primary: 'var' },
    // `includes` takes `contains`' word: both engines read them as one operator,
    // and `dahil` is `inclusive` (pick's range word), which the reader takes it for.
    includes: { primary: 'içerir' },
    is: { primary: 'dir' },
    live: { primary: 'canlı' },
    matches: { primary: 'eşleşir' },
    not: { primary: 'değil' },
    or: { primary: 'veya' },
    otherwise: { primary: 'aksi_halde' },
    then: { primary: 'ardından' },
    when: { primary: 'iken' },
    where: { primary: 'nerede' },
  },
  temporal: {
    h: { primary: 'sa' },
    hour: { primary: 'saat' },
    hours: { primary: 'saat' },
    millisecond: { primary: 'milisaniye' },
    milliseconds: { primary: 'milisaniye' },
    min: { primary: 'dk' },
    minute: { primary: 'dakika' },
    minutes: { primary: 'dakika' },
    ms: { primary: 'ms' },
    s: { primary: 's' },
    second: { primary: 'saniye' },
    seconds: { primary: 'saniye' },
    times: { primary: 'kez' },
  },
  values: {
    body: { primary: 'gövde' },
    detail: { primary: 'detay' },
    document: { primary: 'belge' },
    element: { primary: 'öğe' },
    event: { primary: 'olay' },
    false: { primary: 'yanlış' },
    it: { primary: 'o' },
    its: { primary: 'onun' },
    me: { primary: 'ben' },
    my: { primary: 'benim' },
    myself: { primary: 'kendim' },
    null: { primary: 'null' },
    result: { primary: 'sonuç' },
    target: { primary: 'hedef' },
    true: { primary: 'doğru' },
    undefined: { primary: 'tanımsız' },
    value: { primary: 'değer' },
    window: { primary: 'pencere' },
    you: { primary: 'sen' },
    your: { primary: 'senin' },
    yourself: { primary: 'kendin' },
  },
  attributes: {
    attribute: { primary: 'özellik' },
    attributes: { primary: 'özellikler' },
    class: { primary: 'sınıf' },
    classes: { primary: 'sınıflar' },
    properties: { primary: 'özellikler' },
    property: { primary: 'özellik' },
    style: { primary: 'stil' },
    styles: { primary: 'stiller' },
  },
  expressions: {
    at: { primary: 'de' },
    characters: { primary: 'karakterler' },
    children: { primary: 'çocuklar' },
    closest: { primary: 'enyakın' },
    empty: { primary: 'boş' },
    'ends with': { primary: 'ile biter' },
    exclusive: { primary: 'hariç' },
    first: { primary: 'ilk' },
    'ignoring case': { primary: 'büyük küçük harf duyarsız' },
    inclusive: { primary: 'dahil' },
    'joined by': { primary: 'ile birleştirilmiş' },
    last: { primary: 'sonuncu' },
    'mapped to': { primary: 'ye dönüştürülmüş' },
    next: { primary: 'sonraki' },
    no: { primary: 'yok' },
    parent: { primary: 'ebeveyn' },
    prev: { primary: 'önc' },
    previous: { primary: 'önceki' },
    random: { primary: 'rastgele' },
    some: { primary: 'bazı' },
    'sorted by': { primary: 'göre sıralı' },
    'split by': { primary: 'ile bölünmüş' },
    'starts with': { primary: 'ile başlar' },
    within: { primary: 'içinde' },
  },
};

registerLexicon('tr', turkishLexicon);
